import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Plus, Edit, Trash2, Save, DollarSign, X } from 'lucide-react';

export default function AdminBillingPackagesTab({
  creditPackages, showPackageForm, editingPackage, packageForm, setPackageForm, handleSavePackage,
  setShowPackageForm, setEditingPackage, resetPackageForm, startEditPackage, handleDeletePackage,
}) {
  return (
    <>
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Packages de Crédits</h2>
            <Button 
              onClick={() => {
                resetPackageForm();
                setEditingPackage(null);
                setShowPackageForm(true);
              }}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Créer un Package
            </Button>
          </div>

          {/* Formulaire de création/édition de package */}
          {showPackageForm && (
            <Card className="border-purple-500 border-2">
              <CardHeader>
                <CardTitle>
                  {editingPackage ? 'Modifier le Package' : 'Créer un Nouveau Package'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">ID du Package *</label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded"
                      value={packageForm.package_id}
                      onChange={(e) => setPackageForm({...packageForm, package_id: e.target.value})}
                      placeholder="ex: pack_100"
                      disabled={!!editingPackage}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Nombre de Crédits *</label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded"
                      value={packageForm.credits}
                      onChange={(e) => setPackageForm({...packageForm, credits: parseInt(e.target.value)})}
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Prix (€) *</label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded"
                      value={packageForm.price}
                      onChange={(e) => setPackageForm({...packageForm, price: parseFloat(e.target.value)})}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong>Calcul automatique:</strong>
                  </p>
                  <div className="mt-2 space-y-1 text-sm">
                    <p>Prix par crédit: <strong>{packageForm.credits > 0 ? (packageForm.price / packageForm.credits).toFixed(2) : 0}€</strong></p>
                    <p>Économie: <strong>{packageForm.credits > 0 ? (((packageForm.credits - packageForm.price) / packageForm.credits * 100).toFixed(0)) : 0}%</strong></p>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button onClick={handleSavePackage} className="flex-1">
                    <Save className="h-4 w-4 mr-2" />
                    Enregistrer
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowPackageForm(false);
                      setEditingPackage(null);
                      resetPackageForm();
                    }}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Annuler
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Liste des packages */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {Object.entries(creditPackages).map(([packageId, pkg]) => {
              const discount = ((pkg.credits - pkg.price) / pkg.credits * 100).toFixed(0);
              const pricePerCredit = (pkg.price / pkg.credits).toFixed(2);
              
              return (
                <Card key={packageId} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="text-center">
                    <div className="mx-auto w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center mb-2">
                      <DollarSign className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-3xl font-bold">{pkg.credits}</CardTitle>
                    <CardDescription>crédits</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-center">
                      <p className="text-3xl font-bold">{pkg.price}€</p>
                      {discount > 0 && (
                        <Badge className="mt-2 bg-green-700 text-white font-semibold">
                          -{discount}%
                        </Badge>
                      )}
                    </div>

                    <div className="space-y-1 text-xs text-gray-600">
                      <div className="flex justify-between">
                        <span>Prix/crédit:</span>
                        <span className="font-medium">{pricePerCredit}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Valeur nominale:</span>
                        <span className="font-medium">{pkg.credits}€</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => startEditPackage(packageId, pkg)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleDeletePackage(packageId)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
    </>
  );
}
